import { DataCenterFinancialModelForm } from './DataCenterFinancialModelForm';
/**
 * Three-path entry picker (FA pack — FA12 / deferred from PR-A).
 *
 * Wraps the existing FA surfaces (FinancialAssumptionsForm,
 * ExtractionReviewModal, industry-baseline picker) into a single
 * mount point for the SiteLandingPage's Financial Model tab.
 *
 * Three entry paths:
 *   1. Upload a financial model (.xlsx / .xls / .csv) — runs the
 *      three-tier extractor and opens the post-upload review modal.
 *   2. Manual entry — opens the form pre-seeded with the empty FA.
 *   3. Industry-baseline template — applies a tech baseline + opens
 *      the form for follow-up edits.
 *
 * The form's "Save" calls `onCommit(next)` which the host wires to
 * `updateSiteFinancialAssumptions`.
 *
 * Sits ABOVE the Sensitivity + Scenario panels so the user picks an
 * entry path first, then the downstream panels light up.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCloud, PencilLine, Sparkles, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/button';
import { FinancialAssumptionsForm } from './FinancialAssumptionsForm';
import { ExtractionReviewModal } from './ExtractionReviewModal';
import {
  emptyFinancialAssumptions,
  type FinancialAssumptions,
} from '../../../types/financialAssumptions';
import {
  INDUSTRY_BASELINES,
  applyBaselineForce,
  getBaselineStaleness,
  type BaselineKey,
} from '../../../services/financial/industryBaselines';
import {
  extractFinancialModel,
  sha256Hex,
  type ExtractionResult,
} from '../../../services/financial/extractFinancialModel';
import { useStore } from '../../../store';
import type { ProjectIntent } from '../../../types/siteRag';

/**
 * Document-library placement for an uploaded financial model (A11).
 * The workbook must survive the extraction so a report stamped with its
 * hash can be re-opened and re-run against the same model months later.
 */
const FINANCIAL_MODEL_FOLDER = 'Financial';
const FINANCIAL_MODEL_TAG = 'financial-model';

export interface FinancialModelEntryPanelProps {
  /** Current FA on the site (canonical, post-resolver). null when nothing's set yet. */
  fa: FinancialAssumptions | null;
  siteId: string;
  ownerUid?: string;
  scenarioId?: string;
  projectIntent?: ProjectIntent;
  /** Editor identifier (uid / email) — stamped on the saved FA. */
  updatedBy: string;
  /** Called when the user clicks Save in the form. Host commits via store. */
  onCommit: (next: FinancialAssumptions) => void | Promise<void>;
}

type EntryView = 'picker' | 'form' | 'baseline-picker' | 'data-center';

export function FinancialModelEntryPanel(props: FinancialModelEntryPanelProps): React.JSX.Element {
  const { fa, siteId, projectIntent, updatedBy, onCommit, ownerUid, scenarioId } = props;
  const [view, setView] = useState<EntryView>(fa?.businessModelKind === 'data-center-capacity-fee' ? 'data-center' : fa ? 'form' : 'picker');
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [extractionFile, setExtractionFile] = useState<File | null>(null);
  const [extractionFileHash, setExtractionFileHash] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [seed, setSeed] = useState<FinancialAssumptions | null>(fa);

  // ── Forward incoming FA revisions to the form (audit A6 / plan 1.3) ──────
  //
  // `seed` used to be captured ONCE at mount, so every later write to the site
  // record — another tab's save arriving over the LIVE-SITES `onSnapshot`
  // listener, an auto-pull, the BoM CAPEX sync, the cross-validation
  // "Sync to intent" button — was invisible here: the panel kept handing the
  // form its mount-time snapshot, and the next Save wrote that stale record
  // back over the newer one.
  //
  // The panel does not arbitrate: it forwards the new revision, and the FORM
  // decides (silent re-seed when its draft is clean, an explicit
  // "changed elsewhere" choice when it is dirty). `seed` is only a LOCAL
  // pre-save seed (blank form / applied baseline), so it is replaced whenever
  // the incoming `fa` prop itself moves — never when only `seed` changed.
  const seenFaRevisionRef = useRef<string | null>(fa?.updatedAt ?? null);
  useEffect(() => {
    const revision = fa?.updatedAt ?? null;
    if (revision === seenFaRevisionRef.current) return;
    seenFaRevisionRef.current = revision;
    setSeed(fa);
    setView(fa?.businessModelKind === 'data-center-capacity-fee' ? 'data-center' : fa ? 'form' : 'picker');
  }, [fa]);

  const handleManual = useCallback(() => {
    setSeed(fa?.businessModelKind === 'data-center-capacity-fee' ? emptyFinancialAssumptions(new Date().toISOString(), updatedBy) : fa ?? emptyFinancialAssumptions(new Date().toISOString(), updatedBy));
    setView('form');
  }, [fa, updatedBy]);

  const handleBaselinePick = useCallback((key: BaselineKey) => {
    const now = new Date().toISOString();
    const next = applyBaselineForce(fa?.businessModelKind === 'data-center-capacity-fee' ? null : fa, key, now, updatedBy);
    setSeed(next);
    setView('form');
  }, [fa, updatedBy]);

  const handleUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setUploading(true);
    try {
      // Read the bytes ONCE — the hash and the extractor share them, so
      // the pin is provably over the file the numbers came from.
      const bytes = await file.arrayBuffer();
      const hash = await sha256Hex(bytes);
      const result = await extractFinancialModel(bytes);
      setExtraction(result);
      setExtractionFile(file);
      setExtractionFileHash(hash ?? undefined);
      if (!hash) {
        setUploadError(
          'Could not compute a file hash in this browser (WebCrypto unavailable) — '
          + 'the model will be saved by name only, so a future report cannot be pinned to this exact file.',
        );
      }
    } catch (err) {
      setUploadError((err as Error)?.message ?? 'Could not read file.');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleExtractionCommit = useCallback(async (next: FinancialAssumptions) => {
    // A11 — park the workbook in the site's Document Library so the
    // `sourceFileHash` on the FA points at a file the user can re-open.
    // Runs BEFORE the modal is torn down so its "Committing…" state
    // covers the upload instead of the screen going quiet.
    // Best-effort: a storage failure must not lose the reviewed numbers.
    let sourceFileId: string | undefined;
    if (extractionFile) {
      try {
        const doc = await useStore.getState().addSiteDocument(siteId, extractionFile, {
          folder: FINANCIAL_MODEL_FOLDER,
          appendTags: [FINANCIAL_MODEL_TAG],
        });
        sourceFileId = doc?.id;
      } catch (err) {
        setUploadError(
          'Values were saved, but the workbook could not be added to the document library: '
          + `${(err as Error)?.message ?? 'unknown error'}. The model is pinned by hash only.`,
        );
      }
    }
    // E5 - a FAILED save must keep the modal open. `onCommit` used to
    // swallow its error and resolve, so the panel tore the review down and
    // the user's reviewed numbers were gone with only a toast to show for
    // it. It now rethrows, and the teardown below is reached only on success.
    await onCommit(sourceFileId ? { ...next, sourceFileId } : next);
    setExtraction(null);
    setExtractionFile(null);
    setExtractionFileHash(undefined);
    setSeed(next);
    setView('form');
  }, [extractionFile, onCommit, siteId]);

  const handleExtractionDiscard = useCallback(() => {
    setExtraction(null);
    setExtractionFile(null);
    setExtractionFileHash(undefined);
  }, []);

  if (view === 'data-center') return <DataCenterFinancialModelForm initial={fa?.businessModelKind === 'data-center-capacity-fee' ? fa : null}
    siteId={siteId} ownerUid={ownerUid ?? ''} scenarioId={scenarioId} updatedBy={updatedBy} onSave={onCommit} onCancel={() => setView('picker')} />;

  // ── Picker view ──
  // E5 - the gate used to be `view === 'picker' && !fa`, so the moment a
  // model existed the upload and baseline paths became unreachable and the
  // panel's own promise ("You can switch later") was false. `source:
  // 'hybrid'` - a model part-uploaded, part-hand-entered - could not be
  // produced at all. The form view now offers an explicit way back here.
  if (view === 'picker') {
    return (
      <div className="border border-border bg-surface-1 rounded-lg">
        <div className="px-4 py-3 border-b border-border">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Financial Model
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">
            Pick how to populate the project economics. You can switch later.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
          <EntryCard icon={<PencilLine className="w-5 h-5" />} title="Data-center capacity fee"
            description="Annual contracted IT revenue, occupancy, grid costs and staged capital. Separate from generation/PPA economics."
            cta={<Button variant="outline" size="sm" onClick={() => setView('data-center')}>Enter capacity-fee model</Button>} />
          <EntryCard
            icon={<UploadCloud className="w-5 h-5" />}
            title="Upload financial model"
            // E5 - this advertised an "AI fallback". `extractFinancialModel`
            // does accept an `llmExtractor` callback, but this panel calls it
            // with no options, so that tier never runs: the claim described a
            // capability the product does not ship on this path. Describe the
            // two tiers that DO run.
            description="Excel (.xlsx) or CSV. Reads named ranges, then matches row/column labels."
            cta={
              <label className="inline-block w-full">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleUpload(f);
                    e.target.value = '';
                  }}
                />
                <span className="inline-flex w-full items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground type-section cursor-pointer hover:bg-primary/90 rounded-md">
                  {uploading ? 'Reading…' : 'Choose file'}
                </span>
              </label>
            }
          />
          <EntryCard
            icon={<PencilLine className="w-5 h-5" />}
            title="Manual entry"
            description="Build from scratch. Inline editing with provenance chips per field."
            cta={
              <Button variant="outline" size="sm" onClick={handleManual} className="w-full">
                Start blank form
              </Button>
            }
          />
          <EntryCard
            icon={<Sparkles className="w-5 h-5" />}
            title="Industry baseline"
            description="Start from NREL ATB / Lazard LCOE for your technology. Edit downstream."
            cta={
              <Button variant="outline" size="sm" onClick={() => setView('baseline-picker')} className="w-full">
                Pick template
              </Button>
            }
          />
        </div>
        {uploadError && (
          <div className="px-4 pb-3 -mt-2 text-[11px] text-danger flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {uploadError}
          </div>
        )}
        {extraction && extractionFile && (
          <ExtractionReviewModal
            open
            extraction={extraction}
            sourceFileName={extractionFile.name}
            sourceFileHash={extractionFileHash}
            baseAssumptions={fa?.businessModelKind === 'data-center-capacity-fee' ? null : fa}
            updatedBy={updatedBy}
            onCommit={handleExtractionCommit}
            onDiscard={handleExtractionDiscard}
          />
        )}
      </div>
    );
  }

  // ── Baseline picker ──
  if (view === 'baseline-picker') {
    return (
      <div className="border border-border bg-surface-1 rounded-lg">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
              Pick a technology template
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">
              Sources: NREL ATB 2024 · Lazard LCOE v17.0 · EIA AEO 2024. Edits after applying flip provenance chips to manual.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setView('picker')}>
            Back
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
          {(Object.keys(INDUSTRY_BASELINES) as BaselineKey[]).map((key) => {
            const baseline = INDUSTRY_BASELINES[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleBaselinePick(key)}
                className="text-left border border-border hover:border-primary bg-surface-2 hover:bg-state-hover p-3 transition-colors rounded-md"
              >
                <div className="text-[11px] font-semibold text-text-primary">{baseline.label}</div>
                <div className="text-[10px] text-text-muted mt-1">{baseline.description}</div>
                <div className="type-body text-text-muted mt-2 uppercase tracking-widest">
                  Source: {baseline.source}
                </div>
                {/* F6 / 6.1 — VINTAGE, STATED. `getBaselineStaleness` existed
                    with no caller, so a 2024 baseline presented itself as
                    current in 2026: the picker showed a cost basis two vintages
                    old with nothing to say so, and every model seeded from it
                    inherited that silence. */}
                {(() => {
                  const age = getBaselineStaleness(baseline);
                  if (!age.stale || age.ageYears == null) return null;
                  return (
                    <div
                      className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-md border border-warning/50 bg-warning/10 text-warning"
                      data-testid={`fa-baseline-stale-${key}`}
                    >
                      {age.vintageYear} basis &middot; {age.ageYears} yr old
                    </div>
                  );
                })()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Form view (default when FA exists) ──
  return (
    <>
      {/* E5 - ALWAYS-AVAILABLE ENTRY PATHS. With a model in place the picker
          was unreachable, so the panel's "You can switch later" was false: no
          way to upload a revised workbook, no way to re-apply a baseline, and
          `source: 'hybrid'` unreachable. Offered as REPLACE, not as a second
          silent path - the wording says what it will do to the model already
          on screen. */}
      {fa ? (
        <div className="flex items-center justify-end gap-2 mb-2" data-testid="fa-entry-switch-row">
          <span className="text-[10px] text-text-muted mr-auto">
            Replace this model from another source:
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setView('picker')}
            data-testid="fa-entry-replace"
          >
            Upload or pick a baseline
          </Button>
        </div>
      ) : null}
      <FinancialAssumptionsForm
        initial={seed ?? fa}
        siteId={siteId}
        projectIntent={projectIntent}
        onSave={onCommit}
        onCancel={!fa ? () => setView('picker') : undefined}
        updatedBy={updatedBy}
      />
      {extraction && extractionFile && (
        <ExtractionReviewModal
          open
          extraction={extraction}
          sourceFileName={extractionFile.name}
          sourceFileHash={extractionFileHash}
          baseAssumptions={fa?.businessModelKind === 'data-center-capacity-fee' ? null : fa}
          updatedBy={updatedBy}
          onCommit={handleExtractionCommit}
          onDiscard={handleExtractionDiscard}
        />
      )}
    </>
  );
}

function EntryCard(props: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: React.ReactNode;
}): React.JSX.Element {
  const { icon, title, description, cta } = props;
  return (
    <div className="border border-border bg-surface-2 p-3 space-y-2 flex flex-col rounded-md">
      <div className="text-primary">{icon}</div>
      <div className="text-[11px] font-semibold text-text-primary">{title}</div>
      <div className="text-[10px] text-text-muted flex-1">{description}</div>
      <div>{cta}</div>
    </div>
  );
}
